"use client"
import { useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import Image from "next/image"
import LocationSection from "./LocationSection";
import About from "./AboutSection";
import Reviews from "./ReviewsSection";
import Features from "./FeaturesSection";
import GamesSection from "./GamesSection";
import { useTranslations } from "next-intl";

const FAQItem = ({ question, answer, purple }: { question: string; answer: string; purple?: boolean }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="relative backdrop-blur-xs rounded-xl overflow-hidden transition-colors duration-200"
      style={purple
        ? { background: "linear-gradient(135deg, #3b1160 0%, #1a0a2e 60%, #0d0d14 100%)" }
        : { background: "#19131f79", border: "1px solid #7c44e511" }
      }
    >
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <span className="text-white font-poppins font-medium text-base">{question}</span>
        <ChevronDown
          className="shrink-0 text-purple-400 transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          size={20}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: open ? "200px" : "0px" }}
      >
        <p className="px-6 pb-5 text-gray-400 font-poppins text-sm leading-relaxed">{answer}</p>
      </div>
    </div>
  )
}

const Services = () => {
  const t = useTranslations('Services')

  const services = [
    { image: "https://cdn.aurex.lk/Services/games.webp", name: t('game_servers'), href: "/games", comingSoon: false },
    { image: "https://cdn.aurex.lk/Services/discords.webp", name: t('discord_bots'), href: "https://dash.aurex.lk/", comingSoon: false },
    { image: "https://cdn.aurex.lk/Services/vps.webp", name: t('vps_hosting'), href: "/coming-soon", comingSoon: false },
    { image: "https://cdn.aurex.lk/Services/ddos.webp", name: t('ddos_protection'), href: "https://discord.com/invite/aurex", comingSoon: false },
  ]
  
  const faqs = [
    {
      question: t('q1'),
      answer: t('a1'),
    },
    {
      question: t('q2'),
      answer: t('a2'),
    },
    {
      question: t('q3'),
      answer: t('a3'),
    },
    {
      question: t('q4'),
      answer: t('a4'),
    },
    {
      question: t('q5'),
      answer: t('a5'),
    },
  ]
  return (
    <section id="services" className="relative z-10 w-full bg-black pt-24 overflow-hidden">
      <div className="absolute inset-x-0 h-[1px] w-full mask-x-from-50% top-0" style={{ "backgroundColor": "#4d0da088" }}></div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 40% 0%, #7c44e51a 0%, transparent 50%, transparent 100%)",
        }}
      />
      <div className="">
        <div className="pb-32 grid grid-cols-2 sm:flex sm:flex-row sm:flex-wrap gap-4 sm:gap-8 justify-items-center sm:justify-center px-4 sm:px-0">
          {services.map((s) =>
            s.href ? (
              <a 
                key={s.name} 
                href={s.href} 
                target={s.href.startsWith('http') ? "_blank" : undefined}
                rel={s.href.startsWith('http') ? "noopener noreferrer" : undefined}
                className="flex flex-col items-center gap-4 group cursor-pointer"
              >
                <div className="rounded-2xl overflow-hidden transition-transform duration-300 group-hover:scale-110">
                  <Image src={s.image} alt={s.name} width={256} height={256} className="w-32 sm:w-64 h-auto object-contain" style={{ height: "auto" }} />
                </div>
                <span className="text-gray-300 font-akira text-[10px] sm:text-sm font-medium text-center">{s.name}</span>
              </a>
            ) : (
              <div key={s.name} className="relative flex flex-col items-center gap-4 cursor-not-allowed select-none">
                <div className="rounded-2xl overflow-hidden opacity-40 grayscale">
                  <Image src={s.image} alt={s.name} width={256} height={256} className="w-32 sm:w-64 h-auto object-contain" style={{ height: "auto" }} />
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <span className="text-gray-600 font-akira text-[10px] sm:text-sm font-medium text-center">{s.name}</span>
                  <span className="text-[8px] sm:text-[10px] font-medium px-1.5 sm:px-2 py-0.5 rounded-tl-md rounded-br-md bg-purple-500/10 border border-purple-500/20 text-purple-400 uppercase tracking-wider font-poppins">
                    {t('coming_soon')}
                  </span>
                </div>
              </div>
            )
          )}
        </div>
        <Features />

        <div className="">
          <Reviews />
        </div>
        <GamesSection />



        <LocationSection />

        <section className="relative px-4 sm:px-8 flex flex-col lg:flex-row gap-16">

          <div
            className="absolute top-0 inset-x-0 h-[1px] w-full mask-x-from-50%"
            style={{ backgroundColor: "#6d17b367" }}
          ></div>

          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundImage: "radial-gradient(ellipse at 40% 0px, #7c44e52a 0%, transparent 60%, transparent 100%)" }}
          />


          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:gap-16">

            <div className="mb-16 lg:w-3/5 flex lg:mt-16 flex-col gap-3">
              {faqs.map((faq, i) => (
                <FAQItem
                  key={faq.question}
                  question={faq.question}
                  answer={faq.answer}
                  purple={i === 2}
                />
              ))}
            </div>
            <div className="mb-16 lg:w-2/5 mt-16 flex flex-col gap-4">
              <div className="relative">
                <svg width="300" height="69" viewBox="0 0 638 69" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 0H603.5C622.554 0 638 15.4462 638 34.5V34.5C638 53.5538 622.554 69 603.5 69H0V0Z" fill="url(#paint0_linear_209_98)" />
                  <defs>
                    <linearGradient id="paint0_linear_209_98" x1="197" y1="35" x2="638" y2="33.5361" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#6A1FA3" stopOpacity="0" />
                      <stop offset="1" stopColor="#7412BF" stopOpacity="0.24" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              <h2 className="text-3xl md:text-3xl font-bold font-akira text-white leading-tight">
                {t('faq_title')}
              </h2>

              <p className="text-gray-400 font-poppins text-base leading-relaxed">
                {t('faq_subtitle')}
              </p>

              <a 
                href="/contact-us"
                className="mt-2 bg-gradient-to-r from-transparent via-[#8B5CF6]/60 to-[#8B5CF6] text-white px-8 py-3 rounded-r-full font-semibold text-sm hover:to-[#7C3AED] transition-colors font-poppins flex items-center gap-2 group w-fit cursor-pointer"
              >
                {t('faq_btn')}
                <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>

        </section>
      </div>

    </section>
  )
}

export default Services
